import type { ApiResponse } from '@troon/api-client-deprecated';

export type AccessProduct = ApiResponse<'get', '/v0/troon-access-subscriptions/products'>['products'][number];
export type AccessProductType = AccessProduct['type'];
export type UpgradeProductType = 'TROON_ACCESS_PLUS' | 'TROON_ACCESS_ELITE';
export type UpgradeAccessProduct = AccessProduct & { type: UpgradeProductType };

type UpgradeProductSlug = 'premium-plus' | 'elite';

const upgradeProductTypesBySlug: Record<UpgradeProductSlug, UpgradeProductType> = {
	'premium-plus': 'TROON_ACCESS_PLUS',
	elite: 'TROON_ACCESS_ELITE',
};

const upgradeProductSlugsByType: Record<UpgradeProductType, UpgradeProductSlug> = {
	TROON_ACCESS_PLUS: 'premium-plus',
	TROON_ACCESS_ELITE: 'elite',
};

const upgradeProductOrder: Record<UpgradeProductType, number> = {
	TROON_ACCESS_PLUS: 0,
	TROON_ACCESS_ELITE: 1,
};

const productRank: Record<AccessProductType, number> = {
	TROON_ACCESS: 0,
	TROON_ACCESS_PLUS: 1,
	TROON_ACCESS_ELITE: 2,
};

export function getUpgradeProductType(slug: string | undefined): UpgradeProductType | undefined {
	return slug && slug in upgradeProductTypesBySlug ? upgradeProductTypesBySlug[slug as UpgradeProductSlug] : undefined;
}

export function getUpgradeProductHref(productType: UpgradeProductType) {
	return `/membership/upgrade/${upgradeProductSlugsByType[productType]}`;
}

export function isUpgradeProductType(
	productType: AccessProductType | null | undefined,
): productType is UpgradeProductType {
	return productType === 'TROON_ACCESS_PLUS' || productType === 'TROON_ACCESS_ELITE';
}

export function canUpgradeToProduct(
	currentProductType: AccessProductType | null | undefined,
	targetProductType: AccessProductType | null | undefined,
) {
	return (
		!!currentProductType &&
		!!targetProductType &&
		targetProductType !== 'TROON_ACCESS' &&
		productRank[targetProductType] > productRank[currentProductType]
	);
}

function getDefaultUpgradeProductType(
	currentProductType: AccessProductType | null | undefined,
): UpgradeProductType | undefined {
	if (canUpgradeToProduct(currentProductType, 'TROON_ACCESS_PLUS')) {
		return 'TROON_ACCESS_PLUS';
	}
	if (canUpgradeToProduct(currentProductType, 'TROON_ACCESS_ELITE')) {
		return 'TROON_ACCESS_ELITE';
	}
	return undefined;
}

export function getDefaultUpgradeProductHref(currentProductType: AccessProductType | null | undefined) {
	const productType = getDefaultUpgradeProductType(currentProductType);
	return productType ? getUpgradeProductHref(productType) : undefined;
}

export function getAvailableUpgradeProducts(
	currentProductType: AccessProductType | null | undefined,
	products: Array<AccessProduct> | null | undefined,
) {
	return (products ?? [])
		.filter(
			(product): product is UpgradeAccessProduct =>
				isUpgradeProductType(product.type) && canUpgradeToProduct(currentProductType, product.type),
		)
		.toSorted((left, right) => upgradeProductOrder[left.type] - upgradeProductOrder[right.type]);
}

export function useGuestPassUpsell() {
	return {
		get isEliteUpgrade() {
			return true;
		},
		get href() {
			return getUpgradeProductHref('TROON_ACCESS_ELITE');
		},
		get cta() {
			return 'Upgrade now';
		},
		get shortCta() {
			return 'Upgrade to Elite';
		},
		get title() {
			return 'Upgrade to Access Premium Elite';
		},
		get productName() {
			return 'Access Premium Elite';
		},
		get productType() {
			return 'troonAccess';
		},
	};
}
